import React, { useState, useEffect } from 'react';
import {
  LuPlay,
  LuPause,
  LuRotateCcw,
  LuTrash2,
  LuPlus,
  LuGitBranch,
  LuClock,
  LuCheckCircle,
  LuXCircle,
  LuAlertCircle,
  LuExternalLink,
  LuCopy,
  LuLoader2,
  LuDownload,
  LuDatabase
} from 'react-icons/lu';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebaseConfig';

const Deployments = ({ node, config, onConfigChange, loading, error, onSave, onDelete, saved, moduleName, institution }) => {
  const [deployments, setDeployments] = useState([]);
  const [scrapeResults, setScrapeResults] = useState([]);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);
  const [nodeConfig, setNodeConfig] = useState({});
  const [isRunningDemo, setIsRunningDemo] = useState(false);
  const [demoError, setDemoError] = useState(null);
  const [demoSuccess, setDemoSuccess] = useState(null);

  // Load node configuration
  useEffect(() => {
    const loadNodeConfig = async () => {
      if (!institution?.id || !moduleName || !node?.id) {
        console.log('Missing required data for loadNodeConfig:', {
          institutionId: institution?.id,
          moduleName,
          nodeId: node?.id
        });
        return;
      }

      try {
        console.log('Loading node config for:', {
          institutionId: institution.id,
          moduleName,
          nodeId: node.id
        });

        const docRef = doc(db, `colleges/${institution.id}/configs/${moduleName}/nodes/${node.id}`);
        const configSnap = await getDoc(docRef);

        if (configSnap.exists()) {
          const configData = configSnap.data();
          console.log('Node config loaded:', configData);
          setNodeConfig(configData);
        } else {
          console.log('No config document found at path:', docRef.path);
          setNodeConfig({});
        }
      } catch (error) {
        console.error('Error loading node config:', error);
        setDeployError(`Error loading configuration: ${error.message}`);
      }
    };

    loadNodeConfig();
  }, [institution?.id, moduleName, node?.id]);

  // Listen for scrape results
  useEffect(() => {
    if (!institution?.id || !node?.id) return;

    const resultsQuery = query(
      collection(db, `colleges/${institution.id}/scrapeResults`),
      where('nodeId', '==', node.id),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(resultsQuery, (snapshot) => {
      const results = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setScrapeResults(results);
    });

    return () => unsubscribe();
  }, [institution?.id, node?.id]);

  const isConfigurationComplete = () => {
    const complete = nodeConfig.scrapeURL && nodeConfig.fieldMappings && nodeConfig.status === 'configured';
    return complete;
  };

  const handleDeploy = async () => {
  if (!isConfigurationComplete()) {
    const error = 'Please complete the configuration in Settings first';
    console.log('Configuration incomplete:', error);
    setDeployError(error);
    return;
  }

  setIsDeploying(true);
  setDeployError(null);

  try {
    const functions = getFunctions();
    const runScraper = httpsCallable(functions, 'runIntelligentScraper');

    // Pass configuration directly like events scraper
    const deploymentData = {
      scrapeURL: nodeConfig.scrapeURL,
      fieldMappings: nodeConfig.fieldMappings,
      moduleType: moduleName,
      institutionId: institution.id,
      nodeId: node.id,
      outputFormat: nodeConfig.outputFormat || 'json'
    };

    console.log('Sending deployment request:', deploymentData);

    const result = await runScraper(deploymentData);

    console.log('Scraping completed:', result.data);
  } catch (error) {
    console.error('Error during deployment:', error);
    setDeployError(error.message || 'Deployment failed');
  } finally {
    setIsDeploying(false);
  }
};

  const handleRunDemoScraper = async () => {
    console.log('Debug - handleRunDemoScraper called with:', {
      institution: institution,
      moduleName: moduleName,
      node: node
    });

    if (!institution?.id || !moduleName || !node?.id) {
      const error = `Missing required configuration data: institution.id=${institution?.id}, moduleName=${moduleName}, node.id=${node?.id}`;
      console.error(error);
      setDemoError(error);
      return;
    }

    setIsRunningDemo(true);
    setDemoError(null);
    setDemoSuccess(null);

    try {
      const functions = getFunctions();
      const runDemoScraper = httpsCallable(functions, 'runDemoScraper');

      const nodeFirestorePath = `colleges/${institution.id}/configs/${moduleName}/nodes/${node.id}`;
      
      const payload = {
        nodeFirestorePath: nodeFirestorePath,
        institution: institution.id
      };

      console.log('Running demo scraper with payload:', payload);

      const result = await runDemoScraper(payload);

      console.log('Demo scraper completed:', result.data);
      setDemoSuccess(`Demo scraper completed successfully! Created document: ${result.data.documentCreated}`);
    } catch (error) {
      console.error('Error running demo scraper:', error);
      setDemoError(error.message || 'Demo scraper failed');
    } finally {
      setIsRunningDemo(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatDataSize = (data) => {
    if (!data) return '0 B';
    const size = JSON.stringify(data).length;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const downloadResult = (result) => {
    if (!result.result?.data) return;

    let content = '';
    let filename = '';
    let mimeType = '';

    const outputFormat = result.config?.outputFormat || 'json';

    switch (outputFormat) {
      case 'json':
        content = JSON.stringify(result.result.data, null, 2);
        filename = `scrape-result-${result.id}.json`;
        mimeType = 'application/json';
        break;
      case 'csv':
        content = result.result.data;
        filename = `scrape-result-${result.id}.csv`;
        mimeType = 'text/csv';
        break;
      case 'text':
        content = result.result.data;
        filename = `scrape-result-${result.id}.txt`;
        mimeType = 'text/plain';
        break;
      default:
        content = JSON.stringify(result.result.data, null, 2);
        filename = `scrape-result-${result.id}.json`;
        mimeType = 'application/json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="p-6">
      {/* Debug Information */}
      <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
        <h4 className="text-yellow-400 font-medium mb-2">Debug Information</h4>
        <div className="text-sm text-yellow-300 space-y-1">
          <div>Institution ID: {institution?.id || 'undefined'}</div>
          <div>Module Name: {moduleName || 'undefined'}</div>
          <div>Node ID: {node?.id || 'undefined'}</div>
          <div>Node Config Keys: {Object.keys(nodeConfig).join(', ') || 'empty'}</div>
          <div>Scrape URL: {nodeConfig.scrapeURL || 'undefined'}</div>
          <div>Field Mappings: {nodeConfig.fieldMappings ? Object.keys(nodeConfig.fieldMappings).join(', ') : 'undefined'}</div>
          <div>Status: {nodeConfig.status || 'undefined'}</div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-white font-medium text-lg">Data Scraper Deployment</h3>
          <p className="text-white/60 text-sm mt-1">Deploy and monitor your data scraping operations</p>
        </div>
      </div>

      {/* Configuration Status */}
      <div className="mb-6 p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-3 h-3 rounded-full ${isConfigurationComplete() ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
          <h4 className="text-white font-medium">Configuration Status</h4>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-white/60">Target URL:</span>
            <div className="text-white/80 mt-1 truncate">
              {nodeConfig.scrapeURL || 'Not configured'}
            </div>
          </div>
          <div>
            <span className="text-white/60">Output Format:</span>
            <div className="text-white/80 mt-1 uppercase">
              {nodeConfig.outputFormat || 'JSON'}
            </div>
          </div>
        </div>
        
        {nodeConfig.fieldMappings && (
          <div className="mt-3">
            <span className="text-white/60 text-sm">Field Mappings:</span>
            <div className="text-white/80 mt-1 text-sm">
              {Object.keys(nodeConfig.fieldMappings).join(', ')}
            </div>
          </div>
        )}
      </div>

      {/* Deploy Buttons */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4">
          <button 
            onClick={handleDeploy}
            disabled={!isConfigurationComplete() || isDeploying}
            className={`px-6 py-3 rounded-lg transition-colors flex items-center gap-3 ${
              isConfigurationComplete() && !isDeploying
                ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isDeploying ? (
              <>
                <LuLoader2 className="w-5 h-5 animate-spin" />
                Scraping...
              </>
            ) : (
              <>
                <LuPlay className="w-5 h-5" />
                Deploy & Run Scraper
              </>
            )}
          </button>

          {/* Demo Scraper Button */}
          <button 
            onClick={handleRunDemoScraper}
            disabled={isRunningDemo}
            className={`px-6 py-3 rounded-lg transition-colors flex items-center gap-3 ${
              !isRunningDemo
                ? 'bg-green-500 hover:bg-green-600 text-white' 
                : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isRunningDemo ? (
              <>
                <LuLoader2 className="w-5 h-5 animate-spin" />
                Running Demo...
              </>
            ) : (
              <>
                <LuPlay className="w-5 h-5" />
                Run Demo Scraper
              </>
            )}
          </button>
        </div>
        
        {deployError && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
            <LuAlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-red-400 text-sm">{deployError}</p>
          </div>
        )}

        {demoError && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
            <LuAlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-red-400 text-sm">{demoError}</p>
          </div>
        )}

        {demoSuccess && (
          <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2">
            <LuCheckCircle className="w-4 h-4 text-green-400" />
            <p className="text-green-400 text-sm">{demoSuccess}</p>
          </div>
        )}
        
        {!isConfigurationComplete() && (
          <p className="mt-2 text-white/60 text-sm">
            Complete the configuration in Settings to enable deployment
          </p>
        )}
      </div>

      {/* Scrape Results */}
      <div className="space-y-4">
        <h4 className="text-white font-medium flex items-center gap-2">
          <LuDatabase className="w-4 h-4" />
          Scrape Results
        </h4>
        
        {scrapeResults.length === 0 ? (
          <div className="text-center py-8">
            <LuDatabase className="w-12 h-12 text-white/40 mx-auto mb-4" />
            <h5 className="text-white/80 font-medium mb-2">No scraping results yet</h5>
            <p className="text-white/60 text-sm">
              Deploy your scraper to start collecting data
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {scrapeResults.map((result) => (
              <div
                key={result.id}
                className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg hover:bg-[#ffffff08] transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {result.result?.success ? (
                      <LuCheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <LuXCircle className="w-4 h-4 text-red-400" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">
                          {result.result?.success ? 'Success' : 'Failed'}
                        </span>
                        <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                          {result.config?.outputFormat || 'JSON'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-white/60 text-sm">
                        <div className="flex items-center gap-1">
                          <LuClock className="w-3 h-3" />
                          {formatDate(result.timestamp)}
                        </div>
                        <div className="flex items-center gap-1">
                          <LuDatabase className="w-3 h-3" />
                          {formatDataSize(result.result?.data)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {result.result?.success && (
                      <button 
                        onClick={() => downloadResult(result)}
                        className="p-2 hover:bg-[#ffffff10] rounded transition-colors" 
                        title="Download Result"
                      >
                        <LuDownload className="w-4 h-4 text-white/60" />
                      </button>
                    )}
                    <button 
                      onClick={() => copyToClipboard(result.id)}
                      className="p-2 hover:bg-[#ffffff10] rounded transition-colors" 
                      title="Copy Result ID"
                    >
                      <LuCopy className="w-4 h-4 text-white/60" />
                    </button>
                  </div>
                </div>

                {/* Result Details */}
                <div className="grid grid-cols-1 gap-4 text-sm">
                  <div>
                    <span className="text-white/60">Target URL:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-white/80 truncate">
                        {result.config?.scrapeURL}
                      </span>
                      {result.config?.scrapeURL && (
                        <a
                          href={result.config.scrapeURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <LuExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>

                  {result.result?.error && (
                    <div>
                      <span className="text-red-400">Error:</span>
                      <div className="text-red-300 mt-1 text-sm">
                        {result.result.error}
                      </div>
                    </div>
                  )}

                  {result.result?.metadata && (
                    <div>
                      <span className="text-white/60">Method:</span>
                      <div className="text-white/80 mt-1">
                        {result.result.metadata.method}
                      </div>
                    </div>
                  )}
                </div>

                {/* Preview for successful results */}
                {result.result?.success && result.result?.data && (
                  <div className="mt-4 pt-3 border-t border-[#ffffff10]">
                    <span className="text-white/60 text-sm">Data Preview:</span>
                    <div className="mt-1 p-2 bg-[#ffffff05] rounded text-xs font-mono text-white/80 max-h-20 overflow-y-auto">
                      {typeof result.result.data === 'string' 
                        ? result.result.data.substring(0, 200) + (result.result.data.length > 200 ? '...' : '')
                        : JSON.stringify(result.result.data, null, 2).substring(0, 200) + '...'
                      }
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Deployments;